<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverClose } from "reka-ui";

import { useBrandStore, useMessageStore, useRetentionStore } from "@/stores";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { isApiError } from "@/services/Api";
import { toISODuration } from "@/services/UtilitiesService";

import RetentionActionConfirmCase from "./RetentionActionConfirmCase.vue";

withDefaults(defineProps<{ option: UnityViewer.Retention.QuickExtensionOption }>(), {  })
const isInFlight = defineModel<string | undefined>("isInFlight", { required: true });

const brandStore = useBrandStore();
const retentionStore = useRetentionStore();
const caseRouteParams = useCaseRouteParams();

const requireReason = computed(() => brandStore.brandConfig.retention.retentionDurationChangesRequireReason);

const reason = ref("");
const caseRefInput = ref("");

const state = ref<{ status: "idle" } | { status: "loading" } | { status: "error"; message: string }>({
  status: "idle",
});

const isSubmitDisabled = computed(
  () =>
    state.value.status === "loading" ||
    caseRefInput.value !== caseRouteParams.value.casename ||
    isInFlight.value != null,
);

async function applyExtension() {
  if (isSubmitDisabled.value) return;

  try {
    state.value = { status: "loading" };
    isInFlight.value = __props.option.label;

    await retentionStore.updateDuration({
      isIndefinite: false,
      reason: reason.value,
      durationState: "DurationSetViaQuickExtension",
      duration: toISODuration({
        month: __props.option.duration.months ?? 0,
        day: __props.option.duration.days ?? 0,
        hour: __props.option.duration.hours ?? 0,
      }),
    });

    state.value = { status: "idle" };

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Retention duration extended successfully.",
    });
  } catch (error) {
    console.error(error);

    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : "Failed to extend retention duration. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  } finally {
    if (isInFlight.value === __props.option.label) isInFlight.value = undefined;
  }
}
</script>
<template>
  <form class="tw-flex tw-flex-col tw-gap-2" @submit.prevent="applyExtension()">
    <p class="tw-max-w-md tw-text-balance tw-text-sm">
      Extend this case's retention <span class="tw-font-bold">{{ option.label }}</span
      >.
    </p>
    <hr />
    <RetentionActionConfirmCase
      v-model:reason="reason"
      v-model:case-ref="caseRefInput"
      :require-reason="requireReason"
    />
    <p v-if="state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
      {{ state.message }}
    </p>
    <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
      <PopoverClose class="tw-btn-md-white tw-b-r">Cancel</PopoverClose>
      <button type="submit" class="tw-btn-md-gray tw-b-r tw-flex tw-items-center tw-gap-1" :disabled="isSubmitDisabled">
        <i v-if="state.status === 'loading'" aria-hidden="true" class="fas fa-spin fa-circle-notch" />
        Apply Extension
      </button>
    </div>
  </form>
</template>
